import { ref, watch } from "vue"
// @ts-ignore
import store from "@/store"
import {
  AppNameEnum,
  BaseUser,
  RefreshAccessTokenResponse,
  UserApiUpdatePasswordRequest,
  UserTypeEnum,
  VitableStandardAuthenticationResponse
} from "@vitable/asclepius-api-client-ts"

import { version } from "../../../../package.json"
import { useAsclepius } from "@/composables/api/asclepius"

const APP_NAME: AppNameEnum = AppNameEnum.ProviderDashboard
const APP_VERSION: string = version

export interface StandardAuthenticationFormData {
  emailOrPhone: string
  password: string
  twoFactorToken?: string
  isClinicalAdminIn: boolean
}

export function useVitableStandardAuthentication() {
  const { getAuthenticationApi, call, callResult } = useAsclepius()

  const postLoginRedirectPathRef = ref<string>()
  const postLoginRedirectPageNameRef = ref<string>()

  async function standardAuth(
    request: StandardAuthenticationFormData,
    postLoginRedirectPath?: string,
    postLoginRedirectPageName?: string
  ): Promise<void> {
    postLoginRedirectPathRef.value = postLoginRedirectPath
    postLoginRedirectPageNameRef.value = postLoginRedirectPageName
    await call(async () =>
      getAuthenticationApi().authenticateWithStandard({
        vitableStandardAuthenticationRequest: {
          email_or_phone: request.emailOrPhone,
          user_type: request.isClinicalAdminIn ? UserTypeEnum.ClinicalAdmin : UserTypeEnum.NursePractitioner,
          password: request.password,
          two_factor_token: request.twoFactorToken,
          app_name: APP_NAME,
          app_version: APP_VERSION
        }
      })
    )
  }

  watch(callResult, async (newResult) => {
    if (newResult.isSuccess()) {
      const data: VitableStandardAuthenticationResponse = newResult.getData()
      const { user, token, refresh_token, is_pending_2fa_token } = data
      if (!is_pending_2fa_token) {
        store.dispatch("me/login", {
          user: user,
          token: token,
          refreshToken: refresh_token,
          postLoginRedirectPath: postLoginRedirectPathRef.value,
          postLoginRedirectPageName: postLoginRedirectPageNameRef.value
        })
      }
    }
  })

  return {
    standardAuth,
    standardAuthResult: callResult
  }
}

export function useRefreshAccessToken() {
  const { getAuthenticationApi, call, callResult } = useAsclepius()

  const user = ref<BaseUser>()
  const accessToken = ref<string>()
  const refreshToken = ref<string>()

  async function refreshAccessToken(refresh: string): Promise<void> {
    await call(async () =>
      getAuthenticationApi().refreshAccessToken({
        refreshAccessTokenRequest: {
          refresh: refresh,
          app_name: APP_NAME,
          app_version: APP_VERSION
        }
      })
    )
  }

  watch(callResult, async (newResult) => {
    if (newResult.isSuccess()) {
      const data: RefreshAccessTokenResponse = newResult.getData()
      user.value = data.user!
      accessToken.value = data.access!
      refreshToken.value = data.refresh!
    }
  })

  return {
    user,
    accessToken,
    refreshToken,
    refreshAccessToken,
    refreshAccessTokenResult: callResult
  }
}

export function useUpdatePassword() {
  const { getUserApi, call, callResult } = useAsclepius()

  async function updatePassword(newPassword: string): Promise<void> {
    const request: UserApiUpdatePasswordRequest = {
      updatePasswordRequest: {
        new_password: newPassword
      }
    }

    await call(async () => getUserApi().updatePassword(request))
  }

  return {
    updatePassword,
    updatePasswordResult: callResult
  }
}
